import React, { useEffect, useState } from 'react';
import './Help.css';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import AdminCenterHome from '../img/admin-center-home.png';
import AdminCenterIntegratedApps from '../img/admin-center-integrated-apps.png';
import AdminCenterUploadManifest from '../img/admin-center-upload-manifest.png';
import { useT } from '../i18n';
import {
  AddinDeployGuideEn, AddinUpdateGuideEn, CacheRefreshGuideEn, MySignatureGuideEn, BrowserCacheGuideEn,
} from './HelpGuidesEn';

// Same source Settings > Eklenti Bilgileri reads: the manifest we currently
// serve, so the "current version" shown below can never drift from reality.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const MANIFEST_URL = `${API_BASE_URL}/outlook-addin/manifest-validated.xml`;

// adminOnly sections are about deploying/maintaining the add-in — a regular
// user (org_user) only sees the ones that concern their own signature.
// Guides are long-form prose, written out per language: the Turkish ones
// live below, the English ones in HelpGuidesEn.jsx.
const SECTIONS = [
  { id: 'addin-deploy', adminOnly: true },
  { id: 'addin-update', adminOnly: true },
  { id: 'cache-refresh', adminOnly: true },
  { id: 'my-signature' },
  { id: 'browser-cache' },
];

const messages = {
  'addin-deploy': { tr: 'Outlook Eklentisi Nasıl Dağıtılır', en: 'How to Deploy the Outlook Add-in' },
  'addin-update': { tr: 'Outlook Eklentisi Nasıl Güncellenir', en: 'How to Update the Outlook Add-in' },
  'cache-refresh': { tr: 'Güncellemeler Neden Gecikir, Nasıl Hızlandırılır', en: 'Why Updates Are Delayed and How to Speed Them Up' },
  'my-signature': { tr: 'İmzamı Nasıl Seçerim', en: 'How to Choose My Signature' },
  'browser-cache': { tr: 'Tarayıcı Önbelleği Nasıl Temizlenir (Safari, Chrome)', en: 'How to Clear the Browser Cache (Safari, Chrome)' },
  docTitle: { tr: 'Yardım - Signature Manager', en: 'Help - Signature Manager' },
  title: { tr: 'Yardım', en: 'Help' },
  adminHint: {
    tr: 'Outlook eklentisinin dağıtımı ve güncellenmesi, imza seçimi, güncellemelerin neden hemen yansımadığı ve önbelleğin nasıl temizleneceğiyle ilgili kılavuzlar.',
    en: 'Guides on deploying and updating the Outlook add-in, choosing a signature, why updates don’t show up immediately, and how to clear the cache.',
  },
  userHint: {
    tr: 'İmzanızı nasıl seçeceğiniz ve sık karşılaşılan sorunlarla ilgili kılavuzlar.',
    en: 'Guides on choosing your signature and common issues.',
  },
};

// The manifest version we currently serve (shared by both languages' update guide).
function useManifestVersion() {
  const [currentVersion, setCurrentVersion] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(MANIFEST_URL)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((xml) => {
        const doc = new DOMParser().parseFromString(xml, 'application/xml');
        if (!cancelled) setCurrentVersion(doc.querySelector('Version')?.textContent || '');
      })
      .catch(() => { if (!cancelled) setCurrentVersion(''); });
    return () => { cancelled = true; };
  }, []);
  return currentVersion;
}

function AddinUpdateGuideFor({ lang }) {
  const currentVersion = useManifestVersion();
  return lang === 'en'
    ? <AddinUpdateGuideEn currentVersion={currentVersion} />
    : <AddinUpdateGuide currentVersion={currentVersion} />;
}

export default function Help() {
  const { t, lang } = useT(messages);
  const en = lang === 'en';
  useEffect(() => {
    document.title = t('docTitle');
  }, [t]);

  const { user } = useAuth();
  const isAdmin = user?.role === 'org_admin' || user?.role === 'super_admin';
  const sections = SECTIONS.filter(s => isAdmin || !s.adminOnly);
  const [active, setActive] = useState(sections[0].id);

  return (
    <div className="page-stack">
      <div className="help-header">
        <h1>{t('title')}</h1>
        <p className="help-hint">{isAdmin ? t('adminHint') : t('userHint')}</p>
      </div>

      <div className="help-layout">
        <nav className="help-nav">
          {sections.map(s => (
            <button
              key={s.id}
              type="button"
              className={`help-nav-item${active === s.id ? ' active' : ''}`}
              onClick={() => setActive(s.id)}
            >
              {t(s.id)}
            </button>
          ))}
        </nav>

        <div className="help-content">
          {active === 'addin-deploy' && (en ? <AddinDeployGuideEn /> : <AddinDeployGuide />)}
          {active === 'addin-update' && <AddinUpdateGuideFor lang={lang} />}
          {active === 'cache-refresh' && (en ? <CacheRefreshGuideEn /> : <CacheRefreshGuide />)}
          {active === 'my-signature' && (en ? <MySignatureGuideEn isAdmin={isAdmin} /> : <MySignatureGuide isAdmin={isAdmin} />)}
          {active === 'browser-cache' && (en ? <BrowserCacheGuideEn /> : <BrowserCacheGuide />)}
        </div>
      </div>
    </div>
  );
}

function AddinDeployGuide() {
  return (
    <Card className="help-card">
      <h2>Outlook Eklentisi Nasıl Dağıtılır</h2>
      <p className="help-intro">
        Eklenti, Microsoft 365 Admin Center üzerinden merkezi olarak dağıtılır — her kullanıcı
        kendi başına kurmaz, bir kere yapılandırılır ve seçilen kullanıcılara otomatik ulaşır.
      </p>

      <ol className="help-steps">
        <li>
          <strong>Microsoft 365 Admin Center'a girin</strong>
          <p>
            <code>admin.microsoft.com</code> adresinden Global Admin veya Exchange Admin yetkisi
            olan bir hesapla giriş yapın.
          </p>
          <img
            className="help-step-screenshot"
            src={AdminCenterHome}
            alt="Microsoft 365 Admin Center giriş ekranı"
          />
        </li>
        <li>
          <strong>Integrated Apps sayfasını açın</strong>
          <p>
            Sol menüden <em>Settings → Integrated apps</em> yolunu izleyin.
          </p>
          <img
            className="help-step-screenshot"
            src={AdminCenterIntegratedApps}
            alt="Admin Center'da Tümleşik uygulamalar sayfası ve Uygulama al butonu"
          />
        </li>
        <li>
          <strong>Yeni bir uygulama yükleyin</strong>
          <p>
            <em>Upload custom apps</em> seçeneğini kullanın ve manifest URL'sini girin:
          </p>
          <pre className="help-code">https://imza.martinci.com/outlook-addin/manifest-validated.xml</pre>
          <img
            className="help-step-screenshot"
            src={AdminCenterUploadManifest}
            alt="Yeni Uygulama Dağıt panelinde manifest URL'sinin girildiği ekran"
          />
        </li>
        <li>
          <strong>Kullanıcı kapsamını seçin</strong>
          <p>
            İlk kurulumda veya bir olaydan sonra yeniden açarken <strong>tüm organizasyon yerine
            küçük bir pilot grup</strong> (örn. sadece IT ekibi) seçmeniz önerilir. Sorunsuz birkaç
            gün geçince kapsamı kademeli olarak genişletin.
          </p>
        </li>
        <li>
          <strong>Dağıtımı onaylayın</strong>
          <p>
            Onayladıktan sonra Microsoft, eklentiyi seçilen kullanıcıların posta kutularına
            dağıtır. Microsoft'un dokümanına göre eklentinin Outlook'ta görünmesi{' '}
            <strong>24-72 saati</strong> bulabilir; kullanıcıların Outlook'u tamamen kapatıp
            yeniden açması gerekir — bkz.{' '}
            <button type="button" className="help-inline-link" onClick={() => {
              const el = document.querySelector('[data-section="cache-refresh"]');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}>
              güncelleme gecikmesi kılavuzu
            </button>.
          </p>
        </li>
      </ol>
    </Card>
  );
}

function AddinUpdateGuide({ currentVersion }) {
  return (
    <Card className="help-card" data-section="addin-update">
      <h2>Outlook Eklentisi Nasıl Güncellenir</h2>
      <p className="help-intro">
        Eklentide iki tür değişiklik olur ve yalnızca biri kendiliğinden ulaşır.
      </p>

      <ol className="help-steps">
        <li>
          <strong>İçerik değişiklikleri — sizin bir şey yapmanız gerekmez</strong>
          <p>
            Eklenti panelinin görünümü, imza mantığı ve hata düzeltmeleri sunucumuzdan canlı
            yüklenir. Biz yayınladığımızda kullanıcılarınız bir sonraki açılışta (genellikle hemen)
            yeni sürümü görür.
          </p>
        </li>
        <li>
          <strong>Manifest değişiklikleri — Admin Center'da güncellemeniz gerekir</strong>
          <p>
            Manifest, eklentinin tanım dosyasıdır: ribbon'daki buton ne yapar, hangi izinleri ister,
            adı ve ikonu nedir. Microsoft 365 Admin Center bu dosyanın yükleme anındaki bir
            <strong> kopyasını</strong> saklar ve sunucumuzdaki değişikliği kendiliğinden almaz.
            Manifest'i değiştiren bir sürüm yayınladığımızda size haber veririz; aşağıdaki adımları
            uygulamanız gerekir.
          </p>
        </li>
      </ol>

      <div className="help-callout">
        <strong>Yayınlanan güncel manifest sürümü:</strong>{' '}
        {currentVersion === null ? 'yükleniyor…' : currentVersion ? <code>{currentVersion}</code> : 'okunamadı'}
        <br />
        Admin Center'da eklentinin sürümü bundan düşükse eklentiniz güncel değildir.
      </div>

      <h3>Adımlar</h3>
      <ol className="help-steps">
        <li>
          <strong>Admin Center'da eklentiyi bulun</strong>
          <p>
            <code>admin.microsoft.com</code> &gt; <em>Settings → Integrated apps</em> (Ayarlar →
            Tümleşik uygulamalar) sayfasında eklentimizi seçin ve mevcut sürüm numarasına bakın.
          </p>
        </li>
        <li>
          <strong>Güncelle'ye tıklayın</strong>
          <p>
            Eklentiyi seçip <em>Update</em> (Güncelle) düğmesini kullanın ve manifest adresini
            girin (menü adları Microsoft'un portal sürümüne göre küçük farklılıklar gösterebilir):
          </p>
          <pre className="help-code">https://imza.martinci.com/outlook-addin/manifest-validated.xml</pre>
          <p>
            Eklentiyi kaldırıp yeniden eklemenize gerek yoktur; güncelleme mevcut eklentiyi yerinde
            yeniler.
          </p>
        </li>
        <li>
          <strong>Kullanıcılar Outlook'u yeniden başlatsın</strong>
          <p>
            Outlook'un tamamen kapatılıp açılması gerekir. Yeni bir buton veya davranışın
            ribbon'da görünmesi Microsoft'un dokümanına göre <strong>24-72 saati</strong> bulabilir.
            Bu süre sonunda hâlâ eski görünüyorsa{' '}
            <button type="button" className="help-inline-link" onClick={() => {
              const el = document.querySelector('[data-section="cache-refresh"]');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}>
              önbellek temizleme adımlarını
            </button>{' '}
            deneyin.
          </p>
        </li>
      </ol>

      <p className="help-note">
        Not: Eklentinin kimliği (Id) ve alan adı değişmez; bu yüzden güncelleme sırasında
        kullanıcılarınızdan yeniden onay istenmez.
      </p>
    </Card>
  );
}

function CacheRefreshGuide() {
  return (
    <Card className="help-card" data-section="cache-refresh">
      <h2>Güncellemeler Neden Gecikir, Nasıl Hızlandırılır</h2>
      <p className="help-intro">
        Biz sunucuda bir düzeltme yayınladığımızda, bunun kullanıcıların Outlook'unda görünmesi
        değişikliğin türüne göre üç ayrı nedenle gecikebilir.
      </p>

      <ol className="help-steps">
        <li>
          <strong>Eklenti içeriği (panel, imza mantığı, hata düzeltmeleri)</strong>
          <p>
            Sunucumuzdan canlı yüklenir, gecikme yoktur. Yine de eski görüntü sürüyorsa sebep
            genellikle 3. maddedeki yerel önbellektir.
          </p>
        </li>
        <li>
          <strong>Manifest değişiklikleri (buton davranışı, izinler, ad/ikon)</strong>
          <p>
            Bunlar kendiliğinden gelmez: Admin Center eklentinin bir kopyasını saklar ve yönetici
            <em> Güncelle</em> diyene kadar eski kopya kullanılır — bkz.{' '}
            <button type="button" className="help-inline-link" onClick={() => {
              const el = document.querySelector('[data-section="addin-update"]');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}>
              eklenti güncelleme kılavuzu
            </button>. Güncelledikten sonra da Microsoft'un dokümanına göre ribbon'da görünmesi{' '}
            <strong>24-72 saati</strong> bulabilir; bu süre tamamen Microsoft tarafında geçer,
            bizim sunucumuzdan hızlandırılamaz.
          </p>
        </li>
        <li>
          <strong>Kullanıcının kendi bilgisayarındaki yerel önbellek (Wef cache)</strong>
          <p>
            Her Outlook, eklenti dosyalarını ve manifest'i kendi bilgisayarında da saklar. Admin
            Center güncel olsa bile bir kullanıcının Outlook'u eski kopyayı göstermeye devam
            edebilir; bu önbelleği temizlemek yeni kopyayı hemen almasını sağlar.
          </p>
        </li>
      </ol>

      <div className="help-callout help-callout-steps">
        <strong>Klasik Outlook (Windows) için önbellek temizleme adımları:</strong>
        <ol>
          <li>Outlook'u tamamen kapatın.</li>
          <li>
            Windows tuşu + R'ye basıp şunu yazın:{' '}
            <code>%LOCALAPPDATA%\Microsoft\Office\16.0\Wef</code>
          </li>
          <li>Açılan klasördeki tüm dosya ve klasörleri silin.</li>
          <li>Outlook'u yeniden açın.</li>
        </ol>
      </div>

      <div className="help-callout help-callout-steps">
        <strong>Outlook (Mac) için önbellek temizleme adımları:</strong>
        <ol>
          <li>Outlook'u tamamen kapatın (<code>Command + Q</code>).</li>
          <li>
            Finder'da <strong>Git</strong> &gt; <strong>Klasöre Git…</strong> (<code>Shift + Command + G</code>)
            ile aşağıdaki klasörleri sırayla açın ve her birinin <strong>içindekileri</strong> silin
            (klasörün kendisini değil; bulunmayanı atlayın):
            <ul>
              <li><code>~/Library/Containers/com.microsoft.Outlook/Data/Documents/wef</code></li>
              <li><code>~/Library/Containers/com.microsoft.Outlook/Data/Library/Caches</code></li>
              <li><code>~/Library/Containers/com.microsoft.Outlook/Data/Library/Application Support/Microsoft/Office/16.0/Wef</code></li>
              <li><code>~/Library/Containers/com.Microsoft.OsfWebHost/Data</code></li>
            </ul>
          </li>
          <li>Outlook'u yeniden açın.</li>
        </ol>
      </div>

      <p className="help-body">
        Bu adımlar yalnızca bilgisayardaki eski kopyayı temizler. Admin Center'daki manifest
        güncel değilse (2. madde) temizlik tek başına bir şeyi değiştirmez — önce eklentiyi
        güncelleyin.
      </p>
    </Card>
  );
}

function MySignatureGuide({ isAdmin }) {
  return (
    <Card className="help-card" data-section="my-signature">
      <h2>İmzamı Nasıl Seçerim</h2>
      <p className="help-intro">
        İmzanız Outlook'ta yeni e-posta, yanıt ve iletmede otomatik eklenir. Kurum yöneticiniz size
        birden fazla şablon veya dil tanımladıysa hangisinin kullanılacağını siz seçebilirsiniz.
        Seçiminiz tüm cihazlarınızda — Outlook masaüstü, Outlook web, iPhone ve Android — geçerli olur.
      </p>

      <h3>Portaldan (telefon dahil her cihazda)</h3>
      <ol className="help-steps">
        <li>
          <strong>Portala girin</strong>
          <p>
            Tarayıcıda <code>imza.martinci.com</code> adresini açın ve <strong>Microsoft ile Giriş Yap</strong>'a
            basıp şirket e-posta hesabınızla giriş yapın. Ayrı bir şifreye gerek yoktur.
          </p>
        </li>
        <li>
          <strong><Link to="/imzam">İmzam</Link> sayfasını açın</strong>
          <p>Sayfanın üstünde imzanızın şu anki hâlini görürsünüz.</p>
        </li>
        <li>
          <strong>Şablonu ve dili seçin</strong>
          <p>
            <strong>Şablon</strong> ve <strong>Dil</strong> bölümlerinden istediğinize dokunun. Seçiminiz anında
            kaydedilir ve önizleme güncellenir.
          </p>
        </li>
      </ol>

      <h3>Outlook masaüstü veya web'den</h3>
      <ol className="help-steps">
        <li>
          <strong>Yeni bir e-posta açın</strong>
          <p>
            Şeritteki <strong>Martinci</strong> grubunda bulunan <strong>İmza Ekle</strong> düğmesine tıklayın (Outlook web'de
            eklenti simgesi <strong>…</strong> menüsünün altında olabilir); sağda eklenti paneli açılır.
          </p>
        </li>
        <li>
          <strong>İmza Şablonu ve Dil'i seçin</strong>
          <p>
            Seçiminiz kaydedilir. Açık olan e-postadaki imzayı da değiştirmek için <strong>İmzayı Ekle</strong>'ye
            basın — imza tekrar eklenmez, yenisiyle değiştirilir.
          </p>
        </li>
      </ol>

      <div className="help-callout help-callout-steps">
        <strong>Bilmeniz gerekenler</strong>
        <ul>
          <li>Seçiminiz yeni açtığınız e-postalarda geçerli olur; önceden açılmış taslaklar kendiliğinden değişmez.</li>
          <li>
            Outlook'un iPhone ve Android uygulamalarında eklenti paneli yoktur (Microsoft kısıtı). Telefondan seçim
            yapmak için portaldaki İmzam sayfasını kullanın.
          </li>
          <li>
            Şablon veya dil seçeneği görmüyorsanız, yöneticiniz size tek bir imza tanımlamıştır. Başka bir
            şablon kullanmak isterseniz kurum yöneticinize başvurun.
          </li>
        </ul>
      </div>

      {isAdmin && (
        <div className="help-callout help-callout-steps">
          <strong>Yöneticiler için — kullanıcıya seçenek tanımlama</strong>
          <ul>
            <li>
              <Link to="/users">Personeller</Link> sayfasında kişiye tıklayın ve <strong>Seçilebilir Şablonlar</strong>{' '}
              bölümünden kullanıcının seçebileceği şablonları işaretleyin. En az iki şablon işaretlenirse seçim
              görünür.
            </li>
            <li>
              Dil seçenekleri şablonun kendi dil varyantlarından gelir: <Link to="/signatures">İmzalar</Link>'da
              şablonu düzenleyip dil ekleyin.
            </li>
            <li>Kullanıcıya özel imza tanımlanmışsa seçenekler gösterilmez; özel imza her zaman önceliklidir.</li>
          </ul>
        </div>
      )}
    </Card>
  );
}

function BrowserCacheGuide() {
  return (
    <Card className="help-card" data-section="browser-cache">
      <h2>Tarayıcı Önbelleği Nasıl Temizlenir (Safari, Chrome)</h2>
      <p className="help-intro">
        Bu portalda veya Outlook Web'de bir güncelleme görünmüyorsa, tarayıcı eski dosyaları
        önbellekten göstermeye devam ediyor olabilir. Önbelleği temizlemek çoğu zaman sorunu
        anında çözer. Yalnızca <strong>önbellek</strong> temizlemek oturumunuzu kapatmaz; çerezleri
        ve site verilerini de silerseniz portala yeniden giriş yapmanız gerekir.
      </p>

      <div className="help-callout help-callout-steps">
        <strong>Safari (Mac) — yalnızca önbelleği temizleme:</strong>
        <ol>
          <li>
            Safari &gt; <strong>Ayarlar</strong> &gt; <strong>Gelişmiş</strong> sekmesine gidin ve{' '}
            <strong>Web geliştiricileri için özellikleri göster</strong> seçeneğini açın.
          </li>
          <li>
            Menü çubuğunda beliren <strong>Geliştir</strong> menüsünden{' '}
            <strong>Önbellekleri Boşalt</strong>'ı seçin (kısayol: <code>Option + Command + E</code>).
          </li>
          <li>Sayfayı yeniden yükleyin.</li>
        </ol>
      </div>

      <div className="help-callout help-callout-steps">
        <strong>Safari (Mac) — yalnızca bu siteyi temizleme (oturum kapanır):</strong>
        <ol>
          <li>
            Safari &gt; <strong>Ayarlar</strong> &gt; <strong>Gizlilik</strong> &gt;{' '}
            <strong>Web Sitesi Verilerini Yönet…</strong> yolunu izleyin.
          </li>
          <li>Arama kutusuna <code>imza.martinci.com</code> yazın (Outlook Web için <code>outlook.office.com</code>).</li>
          <li><strong>Kaldır</strong> ve ardından <strong>Bitti</strong>'ye tıklayın, sayfayı yeniden yükleyin.</li>
        </ol>
      </div>

      <div className="help-callout help-callout-steps">
        <strong>Safari (iPhone / iPad):</strong>
        <ol>
          <li>
            <strong>Ayarlar</strong> &gt; <strong>Safari</strong> yolunu açın (iOS 18 ve sonrasında{' '}
            <strong>Ayarlar</strong> &gt; <strong>Uygulamalar</strong> &gt; <strong>Safari</strong>).
          </li>
          <li>
            <strong>Geçmişi ve Web Sitesi Verilerini Temizle</strong>'ye dokunup onaylayın. Bu işlem
            tüm sitelerdeki oturumları kapatır.
          </li>
        </ol>
      </div>

      <div className="help-callout help-callout-steps">
        <strong>Chrome (Windows / Mac):</strong>
        <ol>
          <li>
            <code>Ctrl + Shift + Delete</code> tuşlarına basın (Mac'te{' '}
            <code>Command + Shift + Delete</code>) — veya sağ üstteki <strong>⋮</strong> menüsü &gt;{' '}
            <strong>Tarama verilerini sil</strong>.
          </li>
          <li>Zaman aralığı olarak <strong>Tüm zamanlar</strong>'ı seçin.</li>
          <li>
            Yalnızca <strong>Önbelleğe alınmış resimler ve dosyalar</strong> kutusunu işaretli bırakın
            (çerezleri işaretlemezseniz oturumunuz açık kalır).
          </li>
          <li><strong>Verileri sil</strong>'e tıklayın ve sayfayı yeniden yükleyin.</li>
        </ol>
      </div>

      <p className="help-body">
        Hızlı bir alternatif olarak sayfayı önbelleği atlayarak yeniden yükleyebilirsiniz: Chrome'da{' '}
        <code>Ctrl + Shift + R</code> (Mac'te <code>Command + Shift + R</code>); Safari'de{' '}
        <code>Shift</code> tuşuna basılı tutarak yenile düğmesine tıklayın.
      </p>

      <p className="help-note">
        Not: Bu adımlar tarayıcıdaki dosyaları yeniler. Klasik Outlook (Windows uygulaması) eklenti
        dosyalarını tarayıcıdan bağımsız kendi klasöründe saklar; onun için önceki
        "Güncellemeler Neden Gecikir" kılavuzundaki adımları uygulayın.
      </p>
    </Card>
  );
}
